

import React, { useState, useEffect } from 'react';
import { Modal, Button, Badge, Form, Dropdown, Alert, Spinner } from "react-bootstrap";
import axios from "axios";
import config from '../../Common/Configurations/APIConfig';
import DefaultData from '../../Common/CompanyData/DefaultData'; // Import the DefaultData
import './AppointmentList.css';

const AppointmentsList = () => {
    const [appointments, setAppointments] = useState([]);
    const [filteredAppointments, setFilteredAppointments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [salonInfo, setSalonInfo] = useState(null);
    const [staffList, setStaffList] = useState([]);
    const [services, setServices] = useState([]);
    
    // Filter states
    const [selectedStatus, setSelectedStatus] = useState('all');
    const [selectedStaff, setSelectedStaff] = useState('all');
    const [dateFilter, setDateFilter] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [appointmentNoFilter, setAppointmentNoFilter] = useState(''); 
    
    // Modal states
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [selectedAppointment, setSelectedAppointment] = useState(null);
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [appointmentToCancel, setAppointmentToCancel] = useState(null);
    const [btnLoading, setBtnLoading] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [editForm, setEditForm] = useState({
        appointmentId: '',
        salonId: '',
        customerName: '',
        email: '',
        serviceTypeId: '',
        staffId: '',
        startTime: '',
        endTime: '',
        status: '',
        notes: ''
    });
    const [error, setError] = useState('');

    const THEME_COLOR = '#065f46';
    const STATUS_COLORS = {
        'Scheduled': 'primary',
        'Confirmed': 'success',
        'Completed': 'secondary',
        'Cancelled': 'danger',
        'Pending': 'warning',
        'PENDING': 'warning',
        'Noshow': 'dark'
    };

    const loggedInSalonId = localStorage.getItem('salonId');
    const currency = DefaultData.DEFAULTCURRENCY || 'NGN';

    useEffect(() => {
        if (loggedInSalonId) {
            fetchAppointments(loggedInSalonId);
            fetchStaff(loggedInSalonId);
            fetchServices(loggedInSalonId);
        } else {
            setError('No salon selected. Please select a salon first.');
            setLoading(false);
        }
    }, [loggedInSalonId]);

    useEffect(() => {
        applyFilters();
    }, [appointments, selectedStatus, selectedStaff, dateFilter, searchTerm, appointmentNoFilter]);

    const fetchAppointments = async (salonId, params = {}) => {
        try {
            setLoading(true);
            setError('');
            const defaultParams = { pageSize: 100, pageNumber: 1, ...params };
            const res = await axios.get(
                `${config.APIACTIVEURL}${config.GETAPPOINTMENTSBYSALONID}/${salonId}`, 
                { params: defaultParams }
            );
            
            if (res.data && res.data.data) {
                setAppointments(res.data.data);
                setFilteredAppointments(res.data.data);
            } else {
                setAppointments([]);
                setFilteredAppointments([]);
            }
        } catch (error) {
            console.error("Error fetching appointments:", error);
            setError('Failed to load appointments. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const fetchStaff = async (salonId) => {
        try {
              const res = await axios.get(`${config.APIACTIVEURL}${config.GETSTAFFBYSALONID}/${salonId}`);
            setStaffList(res.data.data || []);
        } catch (error) {
            console.error("Error fetching staff:", error);
        }
    };

    const fetchServices = async (salonId) => {
        try {
            const res = await axios.get(config.APIACTIVEURL + config.GETSERVICETYPE, {
                params: { salonId: salonId }
            });
            setServices(res.data?.data || []);
        } catch (error) {
            console.error("Error fetching services:", error);
        }
    };

    const applyFilters = () => {
        let filtered = [...appointments];
        if (selectedStatus !== 'all') filtered = filtered.filter(apt => apt.status === selectedStatus);
        if (selectedStaff !== 'all') filtered = filtered.filter(apt => apt.staffId === selectedStaff);
        if (dateFilter) {
            const filterDate = new Date(dateFilter).toDateString();
            filtered = filtered.filter(apt => new Date(apt.startTime).toDateString() === filterDate);
        }
        if (appointmentNoFilter) {
            const term = appointmentNoFilter.toLowerCase();
            filtered = filtered.filter(apt => apt.appointmentNo?.toLowerCase().includes(term));
        }
        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            filtered = filtered.filter(apt =>
                (apt.user?.name?.toLowerCase() || '').includes(term) ||
                (apt.serviceType?.name?.toLowerCase() || '').includes(term) ||
                (apt.staff?.name?.toLowerCase() || '').includes(term) ||
                (apt.user?.email?.toLowerCase() || '').includes(term) ||
                (apt.appointmentNo?.toLowerCase() || '').includes(term)
            );
        }
        setFilteredAppointments(filtered);
    };

    const handleViewDetails = (appointment) => {
        setSelectedAppointment(appointment);
        setShowDetailsModal(true);
    };

    const handleEditAppointment = (appointment) => {
        setEditForm({
            appointmentId: appointment.appointmentId,
            salonId: appointment.salonId,
            customerName: appointment.user?.name || '',
            email: appointment.user?.email || '',
            serviceTypeId: appointment.serviceTypeId,
            staffId: appointment.staffId,
            startTime: appointment.startTime ? new Date(appointment.startTime).toISOString().slice(0, 16) : '',
            endTime: appointment.endTime ? new Date(appointment.endTime).toISOString().slice(0, 16) : '',
            status: appointment.status,
            notes: appointment.notes || ''
        });
        setShowEditModal(true);
    };

    const handleSaveEdit = async () => {
        try {
            setBtnLoading(true);
            setError('');
            if (!editForm.customerName || !editForm.email || !editForm.startTime) {
                setError('Please fill all required fields');
                setBtnLoading(false);
                return;
            }
            let endTime = editForm.endTime;
            if (!endTime && editForm.startTime) {
                const start = new Date(editForm.startTime);
                start.setHours(start.getHours() + 1);
                endTime = start.toISOString().slice(0, 16);
            }
            const payload = { ...editForm, endTime };
            const res = await axios.put(`${config.APIACTIVEURL}${config.UPDATEAPPPOINTMENT}/${editForm.appointmentId}`, payload);
            if (res.data.success) {
                await fetchAppointments(loggedInSalonId);
                setShowEditModal(false);
                setError('Appointment updated successfully!');
                setTimeout(() => setError(''), 3000);
            } else {
                setError(res.data.message || 'Failed to update appointment');
            }
        } catch (error) {
            setError(error.response?.data?.message || 'Failed to update appointment.');
        } finally { setBtnLoading(false); }
    };

    const handleCancelAppointment = async () => {
        if (!appointmentToCancel) return;
        try {
            setBtnLoading(true);
            setError('');
            const res = await axios.post(`${config.APIACTIVEURL}${config.CANCELAPPPOINTMENT}/${appointmentToCancel.appointmentNo}`);
            if (res.data.success) {
                setAppointments(prev => prev.map(apt => apt.appointmentId === appointmentToCancel.appointmentId ? { ...apt, status: 'Cancelled' } : apt));
                setShowCancelModal(false);
                setAppointmentToCancel(null);
                setError('Appointment cancelled successfully!');
                setTimeout(() => setError(''), 3000);
            } else {
                setError(res.data.message || 'Failed to cancel appointment');
            }
        } catch (error) {
            setError(error.response?.data?.message || 'Failed to cancel appointment.');
        } finally { setBtnLoading(false); }
    };

    const formatTimeOnly = (dateTime) => {
        if (!dateTime) return 'N/A';
        return new Date(dateTime).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
    };

    const formatDateOnly = (dateTime) => {
        if (!dateTime) return 'N/A';
        return new Date(dateTime).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
    };

    const formatDateTime = (dateTime) => {
        if (!dateTime) return 'N/A';
        return formatDateOnly(dateTime) + ' • ' + formatTimeOnly(dateTime);
    };

    const formatDuration = (startTime, endTime) => {
        if (!startTime || !endTime) return 'N/A';
        const diff = Math.round((new Date(endTime) - new Date(startTime)) / (1000 * 60));
        return `${diff} min`;
    };

    const clearFilters = () => {
        setSelectedStatus('all');
        setSelectedStaff('all');
        setDateFilter('');
        setSearchTerm('');
        setAppointmentNoFilter('');
    };

    const handleAppointmentNoSearch = async () => {
        if (!appointmentNoFilter.trim()) {
            await fetchAppointments(loggedInSalonId);
            return;
        }
        try {
            setLoading(true);
            const res = await axios.get(`${config.APIACTIVEURL}Appointments/salon/${loggedInSalonId}`, {
                params: { appointmentNo: appointmentNoFilter.trim(), pageSize: 100, pageNumber: 1 }
            });
            if (res.data && res.data.data) {
                setAppointments(res.data.data);
                setFilteredAppointments(res.data.data);
            }
        } catch (error) { setError('Failed to search appointments.'); } finally { setLoading(false); }
    };
    
    const formatCurrency = (amount) => `${currency} ${amount?.toFixed(2) || '0.00'}`;

    if (!loggedInSalonId) {
        return (
            <div className="page-content" style={{ paddingTop: "80px" }}>
                <div className="container-fluid py-4">
                    <Alert variant="danger" className="text-center">
                        <i className="ri-store-line me-2"></i> No salon selected.
                    </Alert>
                </div>
            </div>
        );
    }

    return (
        <div className="page-content appointments-page-wrapper">
            <div className="container-fluid py-3 py-md-4">
                <div className="card border-0 shadow-sm rounded-4">
                    {/* Header */}
                    <div className="card-header bg-white border-0 py-3 d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3">
                        <h4 className="fw-bold mb-0" style={{ color: '#064e3b' }}>
                            <i className="ri-calendar-2-line me-2"></i> Appointments
                        </h4>
                        <div className="d-flex gap-2">
                            <Button variant="danger" onClick={clearFilters} className="d-flex align-items-center gap-1 btn-sm-mobile" disabled={loading}>
                                <i className="ri-filter-off-line"></i> <span className="d-none d-sm-inline">Clear Filters</span>
                            </Button>
                        </div>
                    </div>

                    {/* Filter Section */}
                    <div className="card-body border-bottom bg-light-subtle">
                        <div className="row g-2 g-md-3 mb-3">
                            <div className="col-12 col-md-4">
                                <label className="form-label small fw-bold">Appointment Number</label>
                                <div className="input-group">
                                    <span className="input-group-text bg-light border-end-0"><i className="ri-search-line text-muted"></i></span>
                                    <input type="text" className="form-control" placeholder="Search ID..." value={appointmentNoFilter} 
                                        onChange={(e) => setAppointmentNoFilter(e.target.value)}
                                        onKeyPress={(e) => e.key === 'Enter' && handleAppointmentNoSearch()} disabled={loading} />
                                </div>
                            </div>
                            <div className="col-12 col-md-4">
                                <label className="form-label small fw-bold">Search</label>
                                <div className="input-group">
                                    <span className="input-group-text bg-light border-end-0"><i className="ri-search-line text-muted"></i></span>
                                    <input type="text" className="form-control border-start-0" placeholder="Customer, service..." value={searchTerm} 
                                        onChange={(e) => setSearchTerm(e.target.value)} disabled={loading} />
                                </div>
                            </div>
                            <div className="col-6 col-md-2">
                                <label className="form-label small fw-bold">Status</label>
                                <Form.Select value={selectedStatus} onChange={(e) => setSelectedStatus(e.target.value)} disabled={loading}>
                                    <option value="all">All</option>
                                    <option value="PENDING">Pending</option>
                                    <option value="Confirmed">Confirmed</option>
                                    <option value="Completed">Completed</option>
                                    <option value="Cancelled">Cancelled</option>
                                </Form.Select>
                            </div>
                            <div className="col-6 col-md-2">
                                <label className="form-label small fw-bold">Staff</label>
                                <Form.Select value={selectedStaff} onChange={(e) => setSelectedStaff(e.target.value)} disabled={loading}>
                                    <option value="all">All</option>
                                    {staffList.map(staff => <option key={staff.staffId} value={staff.staffId}>{staff.name}</option>)}
                                </Form.Select>
                            </div>
                        </div>
                        <div className="d-flex justify-content-between align-items-center mt-2">
                            <small className="text-muted">Showing {filteredAppointments.length} results</small>
                        </div>
                    </div>

                    {/* Content Section */}
                    <div className="card-body p-0">
                        {loading ? (
                            <div className="text-center py-5"><Spinner animation="border" variant="primary" /><p className="mt-2">Loading...</p></div>
                        ) : filteredAppointments.length === 0 ? (
                            <div className="text-center py-5"><i className="ri-calendar-line text-muted fs-1"></i><p>No appointments found</p></div>
                        ) : (
                            <>
                                {/* DESKTOP TABLE VIEW */}
                                <div className="table-responsive d-none d-md-block">
                                    <table className="table table-hover align-middle mb-0">
                                        <thead className="table-light">
                                            <tr className="small text-uppercase">
                                                <th className="ps-4">No</th>
                                                <th>Customer</th>
                                                <th>Service</th>
                                                <th>Staff</th>
                                                <th>Date & Time</th>
                                                <th>Price</th>
                                                <th>Status</th>
                                                <th className="text-center pe-4">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredAppointments.map((appointment) => (
                                                <tr key={appointment.appointmentId} className="hover-row">
                                                    <td className="ps-4 text-primary fw-bold">{appointment.appointmentNo || 'N/A'}</td>
                                                    <td>
                                                        <div className="d-flex align-items-center gap-2">
                                                            <div className="customer-avatar">{appointment.user?.name?.charAt(0)}</div>
                                                            <div>
                                                                <div className="fw-bold">{appointment.user?.name}</div>
                                                                <small className="text-muted d-block">{appointment.user?.phoneNumber}</small>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <div className="fw-bold">{appointment.serviceType?.name}</div>
                                                        <small className="text-muted">{formatDuration(appointment.startTime, appointment.endTime)}</small>
                                                    </td>
                                                    <td><span className="badge bg-light text-dark border">{appointment.staff?.name}</span></td>
                                                    <td>
                                                        <div className="fw-bold">{formatDateOnly(appointment.startTime)}</div>
                                                        <small className="text-muted">{formatTimeOnly(appointment.startTime)}</small>
                                                    </td>
                                                    <td className="fw-bold text-success">{formatCurrency(appointment.serviceType?.price)}</td>
                                                    <td><Badge bg={STATUS_COLORS[appointment.status] || 'secondary'}>{appointment.status}</Badge></td>
                                                    <td className="text-center pe-4">
                                                        <div className="d-flex justify-content-center gap-1">
                                                            <Button variant="primary" size="sm" className="action-btn" onClick={() => handleViewDetails(appointment)}><i className="ri-eye-line"></i></Button>
                                                            <Button variant="warning" size="sm" className="action-btn" onClick={() => handleEditAppointment(appointment)}><i className="ri-pencil-line"></i></Button>
                                                            <Button variant="danger" size="sm" className="action-btn" onClick={() => {setAppointmentToCancel(appointment); setShowCancelModal(true);}}><i className="ri-close-line"></i></Button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>

                                {/* MOBILE CARD VIEW */}
                                <div className="d-md-none p-3">
                                    {filteredAppointments.map((appointment) => (
                                        <div key={appointment.appointmentId} className="mobile-appointment-card mb-3 p-3 shadow-sm border rounded-3 bg-white">
                                            <div className="d-flex justify-content-between align-items-start mb-2">
                                                <div>
                                                    <span className="text-primary fw-bold">#{appointment.appointmentNo}</span>
                                                    <h6 className="mb-0 mt-1 fw-bold text-dark">{appointment.user?.name || 'Anonymous'}</h6>
                                                </div>
                                                <Badge bg={STATUS_COLORS[appointment.status] || 'secondary'}>{appointment.status}</Badge>
                                            </div>
                                            <div className="row g-2 small mb-3">
                                                <div className="col-6"><i className="ri-scissors-line me-1"></i> {appointment.serviceType?.name}</div>
                                                <div className="col-6"><i className="ri-user-star-line me-1"></i> {appointment.staff?.name}</div>
                                                <div className="col-6"><i className="ri-calendar-line me-1"></i> {formatDateOnly(appointment.startTime)}</div>
                                                <div className="col-6"><i className="ri-time-line me-1"></i> {formatTimeOnly(appointment.startTime)}</div>
                                                <div className="col-12 fw-bold text-success mt-2 fs-6">{formatCurrency(appointment.serviceType?.price)}</div>
                                            </div>
                                            <div className="d-flex gap-2">
                                                <Button variant="outline-primary" size="sm" className="flex-grow-1" onClick={() => handleViewDetails(appointment)}><i className="ri-eye-line me-1"></i> Details</Button>
                                                <Button variant="outline-warning" size="sm" className="flex-grow-1" onClick={() => handleEditAppointment(appointment)}><i className="ri-pencil-line me-1"></i> Edit</Button>
                                                <Button variant="outline-danger" size="sm" onClick={() => {setAppointmentToCancel(appointment); setShowCancelModal(true);}}><i className="ri-close-line"></i></Button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </>
                        )}
                    </div>
                </div>

                {/* --- Modals (Kept Original Functionality) --- */}
                
                {/* Details Modal */}
                <Modal show={showDetailsModal} onHide={() => setShowDetailsModal(false)} size="lg" centered>
                    <Modal.Header closeButton className="border-0 pb-2">
                        <Modal.Title className="fw-bold" style={{ color: THEME_COLOR }}><i className="ri-calendar-event-line me-2"></i> Details</Modal.Title>
                    </Modal.Header>
                    <Modal.Body className="pt-0">
                        {selectedAppointment && (
                            <div className="row g-3">
                                <div className="col-md-6">
                                    <div className="card border-0 bg-light p-3 h-100">
                                        <h6 className="fw-bold mb-3" style={{ color: THEME_COLOR }}>CUSTOMER</h6>
                                        <p className="mb-1 fw-bold fs-5">{selectedAppointment.user?.name}</p>
                                        <p className="mb-1 text-muted">{selectedAppointment.user?.email}</p>
                                        <p className="mb-0 text-muted">{selectedAppointment.user?.phoneNumber}</p>
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div className="card border-0 bg-light p-3 h-100">
                                        <h6 className="fw-bold mb-3" style={{ color: THEME_COLOR }}>SERVICE</h6>
                                        <p className="mb-1 fw-bold fs-5">{selectedAppointment.serviceType?.name}</p>
                                        <p className="mb-1">Staff: {selectedAppointment.staff?.name}</p>
                                        <h4 className="fw-bold text-success mt-2">{formatCurrency(selectedAppointment.serviceType?.price)}</h4>
                                    </div>
                                </div>
                                <div className="col-12">
                                    <div className="card border-0 bg-light p-3">
                                        <div className="row g-2">
                                            <div className="col-6 col-md-3">
                                                <small className="text-muted d-block">Start</small>
                                                <span className="fw-bold">{formatDateTime(selectedAppointment.startTime)}</span>
                                            </div>
                                            <div className="col-6 col-md-3">
                                                <small className="text-muted d-block">End</small>
                                                <span className="fw-bold">{formatDateTime(selectedAppointment.endTime)}</span>
                                            </div>
                                            <div className="col-12 mt-2">
                                                <small className="text-muted d-block">Notes</small>
                                                <span>{selectedAppointment.notes || 'No notes provided'}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </Modal.Body>
                    <Modal.Footer className="border-0"><Button variant="danger" onClick={() => setShowDetailsModal(false)}>Close</Button></Modal.Footer>
                </Modal>

                {/* Edit Modal */}
                <Modal show={showEditModal} onHide={() => setShowEditModal(false)} centered size="lg">
                    <Modal.Header closeButton className="border-0 pb-2">
                        <Modal.Title className="fw-bold" style={{ color: THEME_COLOR }}><i className="ri-pencil-line me-2"></i> Edit Appointment</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Form>
                            <div className="row">
                                <div className="col-md-6 mb-3">
                                    <Form.Label>Customer Name *</Form.Label>
                                    <Form.Control type="text" value={editForm.customerName} onChange={(e) => setEditForm({...editForm, customerName: e.target.value})} required />
                                </div>
                                <div className="col-md-6 mb-3">
                                    <Form.Label>Email *</Form.Label>
                                    <Form.Control type="email" value={editForm.email} onChange={(e) => setEditForm({...editForm, email: e.target.value})} required />
                                </div>
                                <div className="col-md-6 mb-3">
                                    <Form.Label>Start Time *</Form.Label>
                                    <Form.Control type="datetime-local" value={editForm.startTime} onChange={(e) => setEditForm({...editForm, startTime: e.target.value})} required />
                                </div>
                                <div className="col-md-6 mb-3">
                                    <Form.Label>End Time *</Form.Label>
                                    <Form.Control type="datetime-local" value={editForm.endTime} onChange={(e) => setEditForm({...editForm, endTime: e.target.value})} required />
                                </div>
                                <div className="col-md-6 mb-3">
                                    <Form.Label>Service</Form.Label>
                                    <Form.Select value={editForm.serviceTypeId} onChange={(e) => setEditForm({...editForm, serviceTypeId: e.target.value})}>
                                        <option value="">Select Service</option>
                                        {services.map(s => <option key={s.serviceTypeId} value={s.serviceTypeId}>{s.name}</option>)}
                                    </Form.Select>
                                </div>
                                <div className="col-md-6 mb-3">
                                    <Form.Label>Staff</Form.Label>
                                    <Form.Select value={editForm.staffId} onChange={(e) => setEditForm({...editForm, staffId: e.target.value})}>
                                        <option value="">Select Staff</option>
                                        {staffList.map(s => <option key={s.staffId} value={s.staffId}>{s.name}</option>)}
                                    </Form.Select>
                                </div>
                                <div className="col-md-6 mb-3">
                                    <Form.Label>Status</Form.Label>
                                    <Form.Select value={editForm.status} onChange={(e) => setEditForm({...editForm, status: e.target.value})}>
                                        <option value="PENDING">Pending</option>
                                        <option value="Confirmed">Confirmed</option>
                                        <option value="Completed">Completed</option>
                                        <option value="Cancelled">Cancelled</option>
                                        <option value="Noshow">Noshow</option>
                                    </Form.Select>
                                </div>
                                <div className="col-12 mb-3">
                                    <Form.Label>Notes</Form.Label>
                                    <Form.Control as="textarea" rows={3} value={editForm.notes} onChange={(e) => setEditForm({...editForm, notes: e.target.value})} />
                                </div>
                            </div>
                        </Form>
                    </Modal.Body>
                    <Modal.Footer className="border-0">
                        <Button variant="secondary" onClick={() => setShowEditModal(false)}>Cancel</Button>
                        <Button variant="primary" onClick={handleSaveEdit} disabled={btnLoading}>
                            {btnLoading ? <Spinner size="sm" className="me-2"/> : 'Save Changes'}
                        </Button>
                    </Modal.Footer>
                </Modal>

                {/* Cancel Modal */}
                <Modal show={showCancelModal} onHide={() => setShowCancelModal(false)} centered>
                    <Modal.Header closeButton className="border-0 pb-2"><Modal.Title className="text-danger">Cancel Appointment</Modal.Title></Modal.Header>
                    <Modal.Body>
                        <p>Are you sure you want to cancel appointment <strong>#{appointmentToCancel?.appointmentNo}</strong>?</p>
                    </Modal.Body>
                    <Modal.Footer className="border-0">
                        <Button variant="primary" onClick={() => setShowCancelModal(false)}>No, Keep</Button>
                        <Button variant="danger" onClick={handleCancelAppointment} disabled={btnLoading}>
                            {btnLoading ? "Processing..." : "Yes, Cancel"}
                        </Button>
                    </Modal.Footer>
                </Modal>

            </div>
        </div>
    );
};

export default AppointmentsList;